import { Card, CardContent } from '../ui/card';
import { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Skeleton } from '../ui/skeleton';

interface StatCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  variant: 'primary' | 'success' | 'warning' | 'danger';
  isLoading?: boolean;
}

export function StatCard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  variant = 'primary',
  isLoading = false
}: StatCardProps) {
  const variants = {
    primary: {
      gradient: 'from-blue-600/30 to-blue-700/30 dark:from-blue-500/30 dark:to-blue-600/30',
      bg: 'bg-blue-100 dark:bg-blue-900',
      text: 'text-blue-800 dark:text-blue-200',
      border: 'border-blue-200 dark:border-blue-800',
      shadow: 'shadow-blue-500/10'
    },
    success: {
      gradient: 'from-emerald-600/30 to-emerald-700/30 dark:from-emerald-500/30 dark:to-emerald-600/30',
      bg: 'bg-emerald-100 dark:bg-emerald-900',
      text: 'text-emerald-800 dark:text-emerald-200',
      border: 'border-emerald-200 dark:border-emerald-800',
      shadow: 'shadow-emerald-500/10'
    },
    warning: {
      gradient: 'from-amber-600/30 to-amber-700/30 dark:from-amber-500/30 dark:to-amber-600/30',
      bg: 'bg-amber-100 dark:bg-amber-900',
      text: 'text-amber-800 dark:text-amber-200',
      border: 'border-amber-200 dark:border-amber-800',
      shadow: 'shadow-amber-500/10'
    },
    danger: {
      gradient: 'from-rose-600/30 to-rose-700/30 dark:from-rose-500/30 dark:to-rose-600/30',
      bg: 'bg-rose-100 dark:bg-rose-900',
      text: 'text-rose-800 dark:text-rose-200',
      border: 'border-rose-200 dark:border-rose-800',
      shadow: 'shadow-rose-500/10'
    }
  };

  return (
    <Card className={cn(
      "stat-card relative overflow-hidden border",
      "bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm",
      "transition-all duration-300 ease-out",
      "hover:translate-y-[-2px]",
      "shadow-lg hover:shadow-xl",
      variants[variant].shadow,
      variants[variant].border
    )}>
      <div className={cn(
        "absolute top-0 right-0 w-40 h-40 rounded-full -mr-16 -mt-16 opacity-25",
        `bg-gradient-to-br ${variants[variant].gradient}`
      )} />
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className={cn(
            "p-3.5 rounded-2xl",
            variants[variant].bg,
            "transition-all duration-300 ease-out",
            "hover:scale-110 hover:rotate-[8deg]",
            "shadow-md"
          )}>
            <Icon className={cn(
              "h-6 w-6",
              variants[variant].text,
              "transition-transform duration-300"
            )} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={cn(
              "text-sm font-medium",
              "text-gray-600 dark:text-gray-300",
              "mb-2"
            )}>
              {title}
            </p>
            <div className="flex items-baseline gap-3 mb-2">
              {isLoading ? (
                <Skeleton className="h-9 w-24" />
              ) : (
                <>
                  <h3 className={cn(
                    "text-2xl md:text-3xl font-bold tracking-tight",
                    "text-gray-900 dark:text-white",
                    "truncate"
                  )}>
                    {value}
                  </h3>
                </>
              )}
            </div>
            <p className={cn(
              "text-sm",
              "text-gray-500 dark:text-gray-400",
              "truncate"
            )}>
              {subtitle}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
